import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Navbar, Nav, Card, Form, Button, ProgressBar } from 'react-bootstrap';
import Profile from './Profile';
import { useNavigate } from "react-router-dom";
import { useAuth } from '../AuthContext';
import UploadExtractCV from './UploadExtractCV';
import { api } from '../apiService';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // Don't forget the CSS!
import { toast } from 'react-toastify';
import { useCookies } from 'react-cookie';
import initcv_svg from '../img/merge.svg';
import rewrite_svg from '../img/regeneration.svg';
import save_svg from '../img/save.svg';
import './animate.css'


const ProfileEditor = () => {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [basicData, setBasicData] = useState({profile_email: user?.email, first_name: '', last_name: '', mobile: '', address: '', industry: '', social: '', extra_info: '', initial_cv: '', api_key: ''});
  const [detailData, setDetailData] = useState(null);
  const [summaryData, setSummaryData] = useState({job_title: '', summary: '', skills: '', languages: '', educations: '', certificates: '', projects: '', awards_publications: ''});
  const [experiencesData, setExperiencesData] = useState([{ id: 1, title: '', company: '', address: '', from: '', to: '', description: '' }]);
  const [avatarPreview, setAvatarPreview] = useState('default-avatar.png');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarChanged, setAvatarChanged] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [cookies, setCookie, removeCookie] = useCookies(['userPrivacyDBConsent']);

  const [isSpinningInitCV, setIsSpinningInitCV] = useState(false);
  const [isSpinningRewrite, setIsSpinningRewrite] = useState(false);
  const [isSpinningSave, setIsSpinningSave] = useState(false);


  const totalSteps = 6;
  const progress = (step / totalSteps) * 100;
  const apiServer = process.env.REACT_APP_API_SERVER_URL ?? '/api/';
  const navigate = useNavigate();

  const nextStep = () => {updateStepComplete(step); setStep(s => Math.min(s + 1, totalSteps))};
  const prevStep = () => setStep(s => Math.max(s - 1, 1));

  const updateStepComplete = (s) => {
    switch(s){
      case 2: updateProfileBasic(); break;
      case 3: if(avatarChanged)updateProfilePicture(); break;
      case 4: updateProfileDetail(); break;
      case 5: updateProfileDetail();
        if(!basicData.initial_cv)updateGenerateInitialCV();
        break;
      }
    }


  // --- Load profile  ---
  const loadProfile = async () => {
    const [data, error] = await api.get('profiles/' + user?.email);
    if (data) fillProfile(data);
  };

  const fillProfile = (data) => {
    setBasicData(data);
    setDetailData(data.detail);
    setSummaryData(data.detail);
    setExperiencesData(data.detail?.experiences ?? [{ id: 1, title: '', company: '', address: '', from: '', to: '', description: '' }]);
    }

  useEffect(() => {
    loadProfile();
  }, []);

  // --- Load profile picture  ---
  const loadProfilePicture = async () => {
    const [data, error] = await api.getblob('profiles/picture?profile_email=' + user?.email);
    if (data) {
      const blob = data.blob;
      const imageUrl = URL.createObjectURL(blob);
      setAvatarPreview(imageUrl);
      setAvatarFile(blob);

      // 3. Clean up the URL when component unmounts to save memory
      return () => URL.revokeObjectURL(imageUrl);
    }
  };
  useEffect(() => {
    loadProfilePicture();
  }, []);

  // -- HANDLE CHANGES

  const handleBasicChange = (e) => {
    const { name, value } = e.target;
    setBasicData(prev => ({ ...prev, [name]: value }));
  };

  const handleProilePictureChange = (e) => {
    const file = e.target.files[0]; // 1. Grab the first file
    if (file) {
      if (file.size > 2000000) {
        alert("File is too large! Please choose an image under 2MB.");
        return;
      }
      const imageUrl = URL.createObjectURL(file);
      setAvatarPreview(imageUrl);
      setAvatarFile(file);
      setAvatarChanged(true);
    }
  };

  const handleSummaryChange = (e) => {
    const { name, value } = e.target;
    setSummaryData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddExperience = () => {
    setExperiencesData([...experiencesData, { id: experiencesData.length+1, title: '', company: '', address: '', from: '', to: '', description: '' }]);
  };

  const handleExperienceChange = (index, e) => {
    const { name, value } = e.target;
    const updatedExperiences = experiencesData.map((exp, i) =>
      i === index ? { ...exp, [name]: value } : exp
    );
    setExperiencesData(updatedExperiences);
  };

  const handleUpdateProfileComplete = () => {
    navigate("/");
  };



  // -- UPDATE

  const updateProfileBasic = async () => {
    setIsSpinningSave(true);
    const [data, error] = await api.put('profiles/',basicData,);
    setIsSpinningSave(false);
  };

  const updateProfilePicture = async () => {
    console.log("updateProfilePicture:");
    const formData = new FormData();
    formData.append('picture', avatarFile);
    formData.append('type', 'image/jpeg');
    const [data, error] = await api.patch('profiles/picture?profile_email='+user?.email, formData,);
    if (data) setAvatarChanged(false);
  };

  const updateProfileDetail = async () => {
    const updatedDetailData = { ...detailData, 
      job_title: summaryData.job_title,
      summary: summaryData.summary,
      skills: summaryData.skills,
      languages: summaryData.languages,
      educations: summaryData.educations,
      certificates: summaryData.certificates,
      projects: summaryData.projects,
      awards_publications: summaryData.awards_publications,
      experiences: experiencesData };
    setDetailData(updatedDetailData);

    const [data, error] = await api.put('profiles/', {profile_email: user?.email, detail: updatedDetailData},);
  };

  const updateGenerateInitialCV = async () => {
    console.log("updateGenerateInitialCV:");
    setIsSpinningInitCV(true);
    const [data, error] = await api.patch('profiles/gen_initial_cv?profile_email='+user?.email,);
    if (data) {
      setBasicData(prev => ({ ...prev, initial_cv: data.initial_cv }));
    }
    setIsSpinningInitCV(false);
  };

  const updateRewriteByAI = async () => {
    console.log("updateRewriteByAI:");
    setIsSpinningRewrite(true);
    const [data, error] = await api.patch('profiles/rewrite_cv_by_ai?profile_email='+user?.email,);
    if (data) {
      setBasicData(prev => ({ ...prev, initial_cv: data.initial_cv }));
    }
    setIsSpinningRewrite(false);
  };


  return (
    <Container fluid className="vh-75 d-flex flex-column p-0 overflow-hidden">
      {/* HEADER - Fixed at top */}
      <Navbar bg="dark" variant="dark" className="px-4 shadow-sm flex-shrink-0">
        <Navbar.Brand href="/">AI724 Job Application Helper</Navbar.Brand>
        <Nav className="ms-auto">
          <Profile />
        </Nav>
      </Navbar>



      {/* Progress Section */}
      <div className="mb-4 text-center">
        <h4 className="text-muted mb-3">Step {step} of {totalSteps}</h4>
        <ProgressBar animated now={progress} variant="primary" style={{ height: '10px' }} />
        <div>* You should continue these steps to generate initial CV at the end. it will be used for every job application to generate a job specific CV/Cover letter by AI.</div>
      </div>

      <Card className="shadow-sm border-0 rounded-4">
        <Card.Body className="p-4 p-md-5">
          {/* Step 1: Upload CV */}
          {step === 1 && (
            <Form>
              <h3 className="mb-4 fw-bold">Upload CV</h3>
              <Row>
                <UploadExtractCV onChange={ (data) => fillProfile(data) } />
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold">CV Extracted Info</Form.Label>
                    <Form.Control className=""
                      as="textarea"
                      rows={8}
                      readOnly
                      style={{ fontFamily: 'monospace', fontSize: '0.85rem', backgroundColor: '#f8f9fa' }}
                      value={JSON.stringify(basicData.detail?.cv_extracted_info, null, 2)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <div style={{ marginTop: '10px' }}>
                    <span className="m-1" style={{color:'red'}}>*</span>
                    <input
                      type="checkbox"
                      id="privacy"
                      disabled={cookies?.userPrivacyDBConsent}
                      checked={cookies?.userPrivacyDBConsent}
                      onChange={(e) => {setCookie('userPrivacyDBConsent', e.target.value, { path: '/' });}}
                    />
                    <label htmlFor="privacy">
                      I agree to the <a href="/privacy-policy.md">Privacy Policy</a> and
                      consent to my data being stored in the database.
                    </label>
                  </div>
                </Col>
              </Row>
            </Form>
          )}

          {/* Step 2: Basic Info */}
          {step === 2 && (
            <Form>
              <h3 className="mb-4 fw-bold">Basic Information</h3>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold">First Name</Form.Label>
                    <Form.Control type="text" name="first_name" value={basicData.first_name} onChange={(e) => handleBasicChange(e)} placeholder="John" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold">Last Name</Form.Label>
                    <Form.Control type="text" name="last_name" value={basicData.last_name} onChange={(e) => handleBasicChange(e)} placeholder="Doe" />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold">Mobile Number</Form.Label>
                    <Form.Control type="tel" name="mobile" value={basicData.mobile} onChange={(e) => handleBasicChange(e)} placeholder="+49 (555) 000-0000" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold">Address</Form.Label>
                    <Form.Control type="text" name="address" value={basicData.address} onChange={(e) => handleBasicChange(e)} placeholder="Reinwaldstr 60, 93426 Roding, DE" />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold">Social</Form.Label>
                    <Form.Control type="text" name="social" value={basicData.social} onChange={(e) => handleBasicChange(e)} placeholder="https://linkedin.com/in/john-doe/" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold">Industry</Form.Label>
                    <Form.Control type="text" name="industry" value={basicData.industry} onChange={(e) => handleBasicChange(e)} placeholder="Technology" />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold">Additional info</Form.Label>
                    <Form.Control as="textarea" name="extra_info" value={basicData?.extra_info} onChange={(e) => handleBasicChange(e)} rows={2} placeholder={"Additional information about you like websites or etc."}/>
                  </Form.Group>
                </Col>
                {/**
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold">API Key</Form.Label>
                    <Form.Control type="text" name="api_key" value={basicData.api_key} onChange={(e) => handleBasicChange(e)} placeholder="You can provide your own API key to use AI unlimited or limited to your own" />
                  </Form.Group>
                </Col>
                */}
              </Row>
            </Form>
          )}

          {/* Step 3: Picture */}
          {step === 3 && (
            <div className="text-center py-4">
              <h3 className="mb-4 fw-bold">Profile Picture</h3>
              <div
                className="mx-auto border border-2 border-dashed rounded-circle d-flex align-items-center justify-content-center bg-light"
                style={{ width: '150px', height: '150px', cursor: 'pointer' }}
              >
                {/* <div className="text-muted">
                  <span className="d-block h1 mb-0">📷</span>
                  <small>Upload</small>
                </div>*/}
                <label htmlFor="avatar-upload" style={{ cursor: 'pointer' }}>
                  <img src={avatarPreview} alt="Avatar" className="rounded-circle" width="100" />
                  <span>Upload</span>
                </label>
                <Form.Control type="file" className="d-none" id="avatar-upload" onChange={(e) => handleProilePictureChange(e)} />
              </div>
            </div>
          )}

          {/* Step 4: Professional Summary */}
          {step === 4 && (
            <Form>
              <h3 className="mb-4 fw-bold">Professional Summary</h3>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold">Job Title</Form.Label>
                <Form.Control type="text" name="job_title" value={summaryData?.job_title} onChange={(e) => handleSummaryChange(e)} rows={4} placeholder={"Senior Software Engineer"}/>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold">About You</Form.Label>
                <Form.Control as="textarea" name="summary" value={summaryData?.summary} onChange={(e) => handleSummaryChange(e)} rows={4} placeholder={"Brief summary of your professional background..."}/>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold">Skills</Form.Label>
                <Form.Control as="textarea" name="skills" value={summaryData?.skills} onChange={(e) => handleSummaryChange(e)} rows={4} placeholder={"- IT & Technical Skills:\nLanguages: Python (Advanced), Java (Intermediate), SQL.\nTools & Frameworks: Docker, Kubernetes, Git, AWS."}/>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold">Languages</Form.Label>
                <Form.Control as="textarea" name="languages" value={summaryData?.languages} onChange={(e) => handleSummaryChange(e)} rows={4} placeholder={"German – A2 (actively improvings)\nEnglish – B2\nPersian – Native"} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold">Educations</Form.Label>
                <Form.Control as="textarea" name="educations" value={summaryData?.educations} onChange={(e) => handleSummaryChange(e)} rows={4} placeholder={"BA (Hons) English Literature, 2:1\nUniversity of Manchester\n2021  - Manchester, UK\n\nDissertation: Women’s Voices in Post-War British Fiction\nRelevant modules: Modernist Literature, Creative Writing, Shakespeare Studies\nManchester Grammar School\n2018  - Manchester, UK"} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold">Certificates</Form.Label>
                <Form.Control as="textarea" name="certificates" value={summaryData?.certificates} onChange={(e) => handleSummaryChange(e)} rows={4} placeholder={"Project Management Professional (PMP) – Project Management Institute (2024)\nCertified Professional in Human Resources (CPHR) – HRCI (2023)"} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold">Projects</Form.Label>
                <Form.Control as="textarea" name="projects" value={summaryData?.projects} onChange={(e) => handleSummaryChange(e)} rows={4} placeholder={""} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold">Awards/Publications</Form.Label>
                <Form.Control as="textarea" name="awards_publications" value={summaryData?.awards_publications} onChange={(e) => handleSummaryChange(e)} rows={4} placeholder={""} />
              </Form.Group>
            </Form>
          )}



          {/* Step 5: Experiences */}
          {step === 5 && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="mb-0 fw-bold">Work Experience</h3>
                <Button variant="outline-success" size="sm" onClick={handleAddExperience}>+ Add Position</Button>
              </div>
              <div>
                {experiencesData.map((exp, index) => (
                  <Card key={exp.id} className="bg-light border-0 mb-3 rounded-3">
                    <Card.Body>
                      <Row>
                        <Col md={8} className="mb-3">
                          <Form.Control type="text" placeholder="Company"  name="company" value={exp.company} onChange={(e) => handleExperienceChange(index, e)}/>
                        </Col>
                        <Col md={4} className="mb-3">
                          <Form.Control type="text" placeholder="Address"  name="address" value={exp.address} onChange={(e) => handleExperienceChange(index, e)}/>
                        </Col>
                        <Col md={8} className="mb-3">
                          <Form.Control type="text" placeholder="Job Title" name="title" value={exp.title} onChange={(e) => handleExperienceChange(index, e)}/>
                        </Col>
                        <Col md={2} className="mb-3">
                          <Form.Control type="month" name="from" value={exp.from} onChange={(e) => handleExperienceChange(index, e)}/>
                        </Col>
                        <Col md={2} className="mb-3">
                          <Form.Control type="month" name="to" value={exp.to} onChange={(e) => handleExperienceChange(index, e)}/>
                        </Col>
                        <Col md={12} className="mb-3">
                          <Form.Control as="textarea" name="description" value={exp.description} onChange={(e) => handleExperienceChange(index, e)}  rows={4} placeholder="Brief summary of your experience background in this company ..." />
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </div>
          )}


          {/* Step 6: initial CV */}
          {step === 6 && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="mb-0 fw-bold">CV</h3>
                <div>
                <Button variant="outline-success" size="sm" onClick={updateGenerateInitialCV}>
                  <img src={initcv_svg} className={isSpinningInitCV ? 'spin' : ''}  height="32px" width="32px" title="Generate Initial CV" /></Button>
                <Button variant="outline-success" size="sm" onClick={updateRewriteByAI} className="mx-3">
                  <img src={rewrite_svg} className={isSpinningRewrite ? 'spin' : ''}  height="32px" width="32px" title="Rewrite by AI" /></Button>
                <Button variant="outline-success" size="sm" onClick={updateProfileBasic}>
                  <img src={save_svg} className={isSpinningSave ? 'spin' : ''}  height="32px" width="32px" title="Save manual changes" /></Button>
                </div>
              </div>
              <div>
                  <Card className="bg-light border-0 mb-3 rounded-3">
                    <Card.Body>
                      <Row>
                        <Col md={12} className="mb-3">
                          <Form.Label className="small fw-semibold">Initial CV</Form.Label>
                          <Form.Control as="textarea" name="initial_cv" value={basicData.initial_cv} onChange={(e) => handleBasicChange(e)} rows={10} placeholder={"Initial CV, it will be tailored by AI regarding every job position you are going to apply later!"}/>
                        </Col>
                      </Row>
                  <div>Note : please don't change the head line covered by ** like **PROFESSIONAL EXPERIENCE**, and don't remove the empty lines between sections, they are important in generating your final documents!
                  </div>
                    </Card.Body>
                  </Card>
              </div>
            </div>
          )}



          {/* Navigation Controls */}
          <div className="d-flex justify-content-between mt-5 pt-4 border-top">
            <Button
              variant="link"
              className="text-decoration-none text-muted fw-semibold"
              onClick={prevStep}
              disabled={step === 1}
            >
              Back
            </Button>
            <Button
              variant="primary"
              className="px-5 rounded-pill fw-bold"
              disabled={!cookies?.userPrivacyDBConsent}
              onClick={step === totalSteps ? () => handleUpdateProfileComplete() : nextStep}
            >
              {step === totalSteps ? 'Finish' : 'Next Step'}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* 3. FIXED FOOTER */}
        <footer className="bg-white border-top p-2 text-center flex-shrink-0">
          <small className="text-muted">© 2025 AI Resume Suite. Designed and Developed by <a href='http://linkedin.com/in/adel-ramezani' target='_blank' className="ml-1 text-gray-300 hover:text-white underline transition">
            Adel Ramezani</a></small>
        </footer>
    </Container>
  );
};

export default ProfileEditor;
